'use client';

import {ComponentProps, useMemo} from 'react';

import {App, AppsResponse, AppStatus} from '@web-catalog/types';

import {useOmicronClientFlag} from '@web-catalog/hooks/omicron';
import useMediaQueryMatch from '@web-catalog/hooks/useMediaQueryMatch';

import {chunk} from '@web-catalog/utils/helpers';

import {AppCard} from '@web-catalog/ui-kit/AppCard';

import {getRandomIntInRange} from '@web-catalog/utils/random';

import {AdWrapper} from './AdWrapper';

type Props = {
  appsData?: AppsResponse | undefined;
  listName: string;
  logCustomAnalyticsOnClick?: ComponentProps<
    typeof AppCard
  >['logCustomAnalyticsOnClick'];
  allowAds?: boolean;
  redesignRating?: boolean;
};

const CHUNK_SIZE = 6;

type AdPlaceholder = {
  type: 'AD';
  isLast?: boolean;
};

const isAdPlaceholder = (item: App | AdPlaceholder): item is AdPlaceholder =>
  'type' in item && item.type === 'AD';

export const AppsList: React.FC<Props> = ({
  appsData,
  listName,
  allowAds,
  redesignRating,
  logCustomAnalyticsOnClick,
}) => {
  const isExtraLargeScreen = useMediaQueryMatch('(min-width: 1920px)');

  const shouldRenderBanners =
    useOmicronClientFlag('WEB_YANDEX_AD_APPS_LIST_ENABLED') && allowAds;

  const itemsToRender = useMemo<Array<App | AdPlaceholder>>(() => {
    const apps = appsData?.body.content || [];
    if (!apps.length) {
      return [];
    }
    const filteredApps = apps.filter(
      (app) => !app.appStatus || app.appStatus === AppStatus.PUBLISHED,
    );

    if (!shouldRenderBanners) {
      return filteredApps;
    }

    // бьем на кусочки, и в кусочки с нечетным индексом подмешиваем плейсхолдеры для рекламы
    const list = chunk<App | AdPlaceholder>(filteredApps, CHUNK_SIZE)
      .map((part, index) => {
        if (index % 2 === 1) {
          const position = getRandomIntInRange(part.length);
          part.splice(position, 0, {type: 'AD'});
        }
        return part;
      })
      .flatMap((x) => x);

    const lastAd = [...list].reverse().find(isAdPlaceholder);

    if (lastAd) {
      lastAd.isLast = true;
    }

    return list;
  }, [appsData, shouldRenderBanners]);

  return (
    <>
      {itemsToRender.map((appOrAd, position) => {
        if (isAdPlaceholder(appOrAd)) {
          if (appOrAd.isLast && !isExtraLargeScreen) {
            return null;
          }
          return <AdWrapper key={`ad-${position}`} position={position} />;
        }
        return (
          <li key={appOrAd.appId}>
            <AppCard
              position={position}
              app={appOrAd}
              listName={listName}
              logCustomAnalyticsOnClick={logCustomAnalyticsOnClick}
              redesignRating={redesignRating}
            />
          </li>
        );
      })}
    </>
  );
};
